import { useMemo, useState } from "react";

import {
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  MenuItem,
  Modal,
  Paper,
  TextField,
  Typography,
} from "@mui/material";

import { CascadeStart, Client } from "types";
import ClientPreferences from "../settlement_types/ClientPreferences";
import CustomNumberInput from "components/CustomNumberInput";
import ClientAdditionalItemsPreferences from "./ClientAdditionalItemsPreferences";

type Props = {
  open: boolean;
  handleOpen: () => void;
  onCreate: (newClient: Client) => void;
  cascadeStarts: CascadeStart[];
  clients: Client[]; 
};

export default function AddClientModal({
  open = true,
  handleOpen,
  onCreate,
  cascadeStarts, 
  clients
}: Props) {
  const DEFAULT_ID = cascadeStarts[0]?.id ?? 1;

  const defaultValues: Omit<Client, "id"> = {
    razon_social: "",
    code: "",
    address: "",
    cuit: "",
    contact: "",
    observations: "",
    is_active: true,
    client_settlement_types: [],
    show_lote: true,
    show_fecha_vencimiento: true,
    show_oc: true,
    cascade_start_id: DEFAULT_ID,
    parent_client_id: null,
    client_additional_items: [],
  };

  const [data, setData] = useState<Omit<Client, "id">>(defaultValues);

  const availableParentClients = useMemo(() => {
    return [...clients]
      .filter((client) => client.is_active && client.parent_client_id == null)
      .sort((a, b) => a.razon_social.localeCompare(b.razon_social));
  }, [clients]);

  const handleChange = <K extends keyof Omit<Client, "id">>(
    name: K,
    value: Omit<Client, "id">[K]
  ) => {
    setData({
      ...data,
      [name]: value,
    });
  };

  const onSubmit = () => {
    const clientPreferencesList =
      data.client_settlement_types
        ?.filter((clientSettlementType) => clientSettlementType.price > 0)
        .map((clientSettlementType) => ({
          ...clientSettlementType,
          minimum_value:
            clientSettlementType.minimum_value === null ||
            clientSettlementType.minimum_value === undefined ||
            clientSettlementType.minimum_value === ("" as unknown as number)
              ? null
              : Number(clientSettlementType.minimum_value),
        })) ?? [];

    const clientAdditionalItems =
      data.client_additional_items
        ?.filter((item) => item.price !== null && item.price !== undefined)
        .map((item) => ({
          ...item,
          price: Number(item.price),
        })) ?? [];

    onCreate({
      ...data,
      client_settlement_types: clientPreferencesList,
      client_additional_items: clientAdditionalItems,
    } as Client);
  };

  const handleClose = () => {
    setData(defaultValues);
    handleOpen();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Paper
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { sm: "90%", md: "60rem" },
          height: "90%",
          overflow: "auto",
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
        }}
      >
        <Grid container columnSpacing={1}>
          <Typography variant="h5" gutterBottom>
            Nuevo cliente
          </Typography>
        </Grid>
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <TextField
              label="Razón Social"
              value={data.razon_social}
              required
              type="text"
              fullWidth
              onChange={(e) => handleChange("razon_social", e.target.value)}
            />
          </Grid>
          <Grid item xs={6}>
            <CustomNumberInput
              label="CUIT"
              value={data.cuit}
              onChange={(e) => handleChange("cuit", e.target.value)}
              required
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Dirección"
              value={data.address}
              type="text"
              required
              fullWidth
              onChange={(e) => handleChange("address", e.target.value)}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Contacto"
              value={data.contact}
              type="text"
              required
              fullWidth
              onChange={(e) => handleChange("contact", e.target.value)}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Codigo"
              value={data.code}
              type="text"
              required
              fullWidth
              onChange={(e) => handleChange("code", e.target.value)}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Observaciones"
              value={data.observations}
              required
              type="text"
              fullWidth
              onChange={(e) => handleChange("observations", e.target.value)}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              select
              fullWidth
              label="Criterio de selección de Lote"
              value={data.cascade_start_id ?? DEFAULT_ID}
              onChange={e => handleChange('cascade_start_id', Number(e.target.value))}
              disabled={cascadeStarts.length === 0}
            >
              {cascadeStarts.map(cs => (
                <MenuItem key={cs.id} value={cs.id}>
                  {cs.description ?? cs.code}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField
              select
              fullWidth
              label="Cliente padre"
              value={data.parent_client_id ?? ""}
              onChange={(e) =>
                handleChange(
                  "parent_client_id",
                  e.target.value === "" ? null : Number(e.target.value)
                )
              }
            >
              <MenuItem value="">
                <em>Sin cliente padre</em>
              </MenuItem>

              {availableParentClients.map((client) => (
                <MenuItem key={client.id} value={client.id}>
                  {client.code} - {client.razon_social}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={4}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={data.is_active}
                  onChange={(e) => handleChange("is_active", e.target.checked)}
                  color="primary"
                />
              }
              label="Cliente activo"
            />
          </Grid>
          <Grid item xs={4}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={data.show_lote}
                  onChange={(e) => handleChange("show_lote", e.target.checked)}
                  color="primary"
                />
              }
              label="Mostrar Lote"
            />
          </Grid>
          <Grid item xs={4}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={data.show_fecha_vencimiento}
                  onChange={(e) => handleChange("show_fecha_vencimiento", e.target.checked)}
                  color="primary"
                />
              }
              label="Mostrar Fecha Vencimiento"
            />
          </Grid>
          <Grid item xs={4}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={data.show_oc}
                  onChange={(e) => handleChange("show_oc", e.target.checked)}
                  color="primary"
                />
              }
              label="Mostrar OC"
            />
          </Grid>
          <Grid item xs={12}>
            <ClientPreferences
              client={data as Client}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12}>
            <ClientAdditionalItemsPreferences
              client={data as Client}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} display="flex" justifyContent="end" gap={1}>
            <Button type="button" variant="outlined" onClick={handleClose}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={
                !data.razon_social ||
                !data.cuit ||
                !data.address ||
                !data.contact ||
                !data.code ||
                !data.observations ||
                !(
                  data.client_settlement_types?.filter(
                    (preference) => preference.is_default
                  ).length === 3
                )
              }
              onClick={() => {
                onSubmit();
                handleClose();
              }}
            >
              Guardar
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </Modal>
  );
}
